import { RealtimePresenceEntry } from '@interfaces/realtime/realtime-envelope.interface';

/**
 * Uma pessoa com o item aberto (todas as abas dela juntas). Entradas sem `userId` (o servidor
 * esconde quem não é do mesmo parceiro como "Equipe Cognito") viram um grupo só por nome.
 */
export interface RealtimePresencePerson {
  /** Id do usuário; para entrada anônima, `anon:{nome}`. */
  userId: string;
  name: string;
  /** Alguma aba dessa pessoa tem alterações não salvas. */
  editing: boolean;
}

/**
 * Textos que mudam conforme o item da tela (demanda, deal...). Usados pelo
 * `app-realtime-presence-banner` (card 3496).
 */
export interface RealtimePresenceTexts {
  /** "nesta demanda" / "neste deal". */
  inSubject: string;
  /** "com esta demanda aberta" / "com este deal aberto". */
  withSubjectOpen: string;
  /** Verbo do aviso de mudança: "salvou alterações" / "atualizou este deal". */
  changeVerb: string;
  /** Título da confirmação de Recarregar com alteração local: "Recarregar a demanda?". */
  reloadConfirmTitle: string;
  /** "a demanda" / "o deal", para as frases de descrição. */
  subjectWithArticle: string;
  /** Aviso de mudança sem autor conhecido: "Esta demanda foi atualizada" / "Este deal foi atualizado". */
  changedWithoutActor: string;
}

export const DEMAND_PRESENCE_TEXTS: RealtimePresenceTexts = {
  inSubject: 'nesta demanda',
  withSubjectOpen: 'com esta demanda aberta',
  changeVerb: 'salvou alterações',
  reloadConfirmTitle: 'Recarregar a demanda?',
  subjectWithArticle: 'a demanda',
  changedWithoutActor: 'Esta demanda foi atualizada',
};

export const DEAL_PRESENCE_TEXTS: RealtimePresenceTexts = {
  inSubject: 'neste deal',
  withSubjectOpen: 'com este deal aberto',
  changeVerb: 'atualizou este deal',
  reloadConfirmTitle: 'Recarregar o deal?',
  subjectWithArticle: 'o deal',
  changedWithoutActor: 'Este deal foi atualizado',
};

const UNKNOWN_PERSON = 'Outra pessoa';

/** Nome para exibir; vazio vira "Outra pessoa". */
export function displayName(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  return trimmed || UNKNOWN_PERSON;
}

/**
 * Junta as entradas de presença por pessoa, tirando o próprio usuário (inclusive as outras abas
 * dele). Quem tem alguma aba em `editing` conta como editando. Ordem: por nome.
 */
export function summarizeOtherPeople(
  entries: readonly RealtimePresenceEntry[] | null | undefined,
  ownUserId: string | null | undefined
): RealtimePresencePerson[] {
  const byUser = new Map<string, RealtimePresencePerson>();
  for (const entry of entries ?? []) {
    if (!entry) {
      continue;
    }
    const userId = (entry.userId ?? '').trim();
    // Só entrada COM id pode ser o próprio usuário; anônima ("Equipe Cognito") nunca é.
    if (userId && ownUserId && userId === ownUserId) {
      continue;
    }
    const key = userId || `anon:${displayName(entry.name)}`;
    const current = byUser.get(key);
    const editing = entry.state === 'editing';
    if (current) {
      current.editing = current.editing || editing;
      if (current.name === UNKNOWN_PERSON && entry.name) {
        current.name = displayName(entry.name);
      }
    } else {
      byUser.set(key, {
        userId: key,
        name: displayName(entry.name),
        editing,
      });
    }
  }
  return Array.from(byUser.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/** "Ana", "Ana e Bruno", "Ana, Bruno e Carla", "Ana, Bruno e mais 2 pessoas". */
export function joinNames(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} e ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} e ${names[2]}`;
  const rest = names.length - 2;
  return `${names[0]}, ${names[1]} e mais ${rest} pessoas`;
}

/**
 * Frase do aviso de presença. Quem edita aparece primeiro, porque é o que pode sobrescrever o
 * trabalho de quem está olhando. `null` quando não há mais ninguém no item.
 */
export function describePresence(
  people: readonly RealtimePresencePerson[],
  texts: RealtimePresenceTexts = DEMAND_PRESENCE_TEXTS
): string | null {
  const editors = people.filter(person => person.editing).map(person => person.name);
  const viewers = people.filter(person => !person.editing).map(person => person.name);
  const sentences: string[] = [];

  if (editors.length === 1) {
    sentences.push(`${editors[0]} tem alterações não salvas ${texts.inSubject}.`);
  } else if (editors.length > 1) {
    sentences.push(`${joinNames(editors)} têm alterações não salvas ${texts.inSubject}.`);
  }

  if (viewers.length === 1) {
    sentences.push(`${viewers[0]} também está ${texts.withSubjectOpen}.`);
  } else if (viewers.length > 1) {
    sentences.push(`${joinNames(viewers)} também estão ${texts.withSubjectOpen}.`);
  }

  return sentences.length ? sentences.join(' ') : null;
}

/** Dados do aviso de mudança (`RealtimeTopicWatcherService.changeNotice`). */
export interface RealtimeChangeNoticeText {
  /** Nome de quem mudou; `null` quando o servidor não manda autor (rotina automática ou parceiro). */
  actorName: string | null;
  /** HH:mm. */
  time: string;
}

/**
 * Título do aviso de mudança: "Fulano atualizou este deal às 10:42" ou, sem autor, "Este deal foi
 * atualizado às 10:42".
 */
export function describeChangeNotice(
  notice: RealtimeChangeNoticeText,
  texts: RealtimePresenceTexts = DEMAND_PRESENCE_TEXTS
): string {
  const actor = (notice.actorName ?? '').trim();
  return actor
    ? `${actor} ${texts.changeVerb} às ${notice.time}`
    : `${texts.changedWithoutActor} às ${notice.time}`;
}
